import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useUser } from '../auth/AuthProvider';

export interface MembershipPlanRow {
  id: string;
  name: string;
  tier: 'trial' | 'free' | 'pro' | 'enterprise' | 'ultimate';
  description: string;
  priceMonthlyCents: number;
  currency: 'EUR' | 'USD';
  durationDays: number | null;
  features: string[];
  callMinutesIncluded: number | null;
  isActive: boolean;
  sortOrder: number;
  // SaaS-Anfänger-Felder (nur die zwei Anfänger-Pläne setzen sie).
  saasKind?: 'subscription' | 'payg' | null;
  overagePerMinuteNetCents?: number | null;
  perMinuteNetCents?: number | null;
  defaultTopUpNetCents?: number | null;
  minTopUpNetCents?: number | null;
  /** Per-user visibility gate. Missing / `'public'` = visible to all;
   *  `'private'` = only visible to UIDs in `allowedUids`. */
  visibility?: {
    mode: 'public' | 'private';
    allowedUids: string[];
  };
  createdAt?: unknown;
  updatedAt?: unknown;
}

export type MembershipPlanInput = Omit<MembershipPlanRow, 'createdAt' | 'updatedAt'>;

const QK = ['admin-membership-plans'] as const;

export function useMembershipPlans() {
  const { user } = useUser();
  return useQuery<MembershipPlanRow[]>({
    queryKey: QK,
    enabled: !!user,
    staleTime: 30_000,
    queryFn: async () => {
      const fn = httpsCallable<unknown, { items: MembershipPlanRow[] }>(
        functions,
        'listMembershipPlans',
      );
      const r = await fn({});
      return r.data.items;
    },
  });
}

export function useUpsertMembershipPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: MembershipPlanInput) => {
      const fn = httpsCallable<MembershipPlanInput, { ok: true; id: string }>(
        functions,
        'upsertMembershipPlan',
      );
      const r = await fn(input);
      return r.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK });
      void qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });
}

/**
 * End-user-friendly companion to `useMembershipPlans`. Returns the
 * subset of plan IDs the caller is currently allowed to see in the
 * public PlanSwitchCard. Server applies the gate; the UI just filters
 * its tiles by checking membership against the returned set.
 */
export function useMyVisiblePlanIds() {
  const { user } = useUser();
  return useQuery<string[]>({
    queryKey: ['my-visible-plans', user?.uid] as const,
    enabled: !!user,
    staleTime: 30_000,
    queryFn: async () => {
      const fn = httpsCallable<unknown, { planIds: string[] }>(
        functions,
        'listMyVisibleMembershipPlans',
      );
      const r = await fn({});
      return r.data.planIds;
    },
  });
}

export function useRenameMembershipPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { fromId: string; toId: string }) => {
      const fn = httpsCallable<typeof input, { ok: true; fromId?: string; toId?: string; unchanged?: boolean }>(
        functions,
        'renameMembershipPlan',
      );
      const r = await fn(input);
      return r.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK });
      void qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });
}

export function useReorderMembershipPlans() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entries: Array<{ id: string; sortOrder: number }>) => {
      const fn = httpsCallable<{ entries: typeof entries }, { ok: true; count: number }>(
        functions,
        'reorderMembershipPlans',
      );
      const r = await fn({ entries });
      return r.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK });
      void qc.invalidateQueries({ queryKey: ['my-visible-plans'] });
    },
  });
}

export function useDeleteMembershipPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (planId: string) => {
      const fn = httpsCallable<{ planId: string }, { ok: true }>(
        functions,
        'deleteMembershipPlan',
      );
      const r = await fn({ planId });
      return r.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK });
    },
  });
}

const TIER_LABEL: Record<MembershipPlanRow['tier'], string> = {
  trial: 'Trial',
  free: 'Free',
  pro: 'Pro',
  enterprise: 'Enterprise',
  ultimate: 'Ultimate',
};

export function tierLabel(tier: MembershipPlanRow['tier']): string {
  return TIER_LABEL[tier];
}

export function formatPlanPrice(plan: MembershipPlanRow): string {
  if (plan.priceMonthlyCents === 0) return 'Kostenlos';
  const amount = plan.priceMonthlyCents / 100;
  const formatted = new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: plan.currency,
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);
  return `${formatted}/Monat`;
}
